// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
import { type AggRequest, type AggResponse, fetchAgg } from '../../../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../../../utils/caching/in-memory';

import {
	jiraFieldsQueryGenerator,
	type SearchPageJiraHydrationQueryVariables,
} from './query-generator';
import { type JiraHydrationData } from './types';

export type { JiraHydrationData, HydratedJiraIssue } from './types';

const cache = inMemoryCache<AggResponse<JiraHydrationData>>();

export const generateCacheKeyForJiraHydratedFields: (
	variables: SearchPageJiraHydrationQueryVariables,
) => string = (variables: SearchPageJiraHydrationQueryVariables): string => {
	const keyObj = {
		issueKeys: variables.issueKeys,
	};

	return generateHashFromVariables(keyObj);
};

// Expire the cache if there are errors or results are empty
const expireWhen = (result: AggResponse<JiraHydrationData>) => {
	const { data, errors } = result;
	if (!data || (errors && errors.length > 0)) {
		return true;
	}
	return data.jira.issuesByKey.length === 0;
};

export type SearchPageJiraHydrationQueryProps = Pick<
	AggRequest<SearchPageJiraHydrationQueryVariables>,
	'aggAbsoluteUrl' | 'variables' | 'headers'
>;

export const fetchJiraFieldsQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: SearchPageJiraHydrationQueryProps) => Promise<AggResponse<JiraHydrationData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
}: SearchPageJiraHydrationQueryProps) => {
	const graphQuery = jiraFieldsQueryGenerator();
	// ensure that includeStatusCategoryId is always initialized with a boolean value to create a valid GQL query
	const { includeStatusCategoryId = false } = variables;
	const variablesWithFlags: SearchPageJiraHydrationQueryVariables = {
		...variables,
		includeStatusCategoryId,
	};

	return cache.inMemoryDecorator(
		generateCacheKeyForJiraHydratedFields(variables),
		() =>
			fetchAgg<SearchPageJiraHydrationQueryVariables, JiraHydrationData>({
				variables: variablesWithFlags,
				aggAbsoluteUrl,
				graphQuery,
				operationName: 'SearchPageJiraFieldHydration',
				headers,
			}),
		expireWhen,
	);
};
